import { sanitizeForLog } from '@/utils/utils'
import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'
import type {
  AccompaniedDrivingThankYouValues,
  DigitalCardThankYouValues,
  IvkThankYouValues,
  ShowOrDeleteQuotes,
  VehicleChangeThankYouValues
} from '@/types/policy/PolicyTypes'
import type { ProcessReturnContext, SelfServiceProcess } from '@/types/SelfServiceProcess'

import {
  deleteQuoteIdFromList,
  fetchAllPoliciesByPartnerProxyId,
  fetchAllQuoteIdsByPartnerProxyId,
  fetchLockedContractsCount,
  fetchPolicyByPolicyId,
  fetchQuoteShowOrDelete,
  sortPoliciesByDateDesc,
  sortPoliciesByStatus
} from '@/stores/policiesService'
import type { Policy } from 'policies-api'
import { DDSLine } from 'policies-api'
import { fetchOdsContractByContractId } from '@/stores/odsService'
import type { LebenVersicherungsVertrag } from 'ods-api'

// timer to reset thank-you values 30s after the page has been mounted
let resetThankYouValuesTimer: ReturnType<typeof setTimeout> | undefined

export function removeEmptyPolices(list: Policy[]) {
  return list.filter((policy) => policy.policyId)
}

export function sortPoliciesList(list: Policy[]) {
  if (!list) return
  // sort all policies by date first
  list.sort(sortPoliciesByDateDesc)
  // sort all policies by status last (to ensure this to be the main sorting)
  list.sort(sortPoliciesByStatus)
  return list
}

// collects all distinct DDSLines contained in the given list of policies
export function extractDdsLines(list: Policy[]): Set<DDSLine> {
  const ddsLines = new Set<DDSLine>()
  for (const policy of list) {
    for (const ddsLine of policy.ddsLines ?? []) {
      ddsLines.add(ddsLine)
    }
  }
  return ddsLines
}

export async function getPolicyByPolicyId(policyId: string): Promise<Policy | null> {
  return await fetchPolicyByPolicyId(policyId)
}

export const usePoliciesStore = defineStore('policies', () => {
  // only store internal values
  const policyIDs: Ref<string[] | null> = ref(null)
  const lockedContractsCount: Ref<number | null> = ref(null)
  const quoteIds: Ref<string[] | null> = ref(null)
  const quotesDetails: Ref<Map<string, ShowOrDeleteQuotes>> = ref(
    new Map<string, ShowOrDeleteQuotes>()
  )
  const odsContracts: Ref<Map<string, LebenVersicherungsVertrag>> = ref(
    new Map<string, LebenVersicherungsVertrag>()
  )

  // all DDSLines contained in the policies loaded via getAllPoliciesByPartnerProxyId
  const availableDdsLines: Ref<Set<DDSLine>> = ref(new Set<DDSLine>())

  const ivkThankYouValues: Ref<IvkThankYouValues | null> = ref(null)
  const digitalCardThankYouValues: Ref<DigitalCardThankYouValues | null> = ref(null)
  const accompaniedDrivingThankYouValues: Ref<AccompaniedDrivingThankYouValues | null> =
    ref(null)
  const vehicleChangeThankYouValues: Ref<VehicleChangeThankYouValues | null> = ref(null)

  // exposed store values
  const policyDetailURL = '/vertraege/policen/<LINE>/<PARTNER_ADDRESS_ID>/<POLICY_ID>'

  // pre-selected policy ids (in-memory, no persistence, single-tab behaviour)
  const preSelectedPolicyIds: Ref<string[]> = ref([])
  const processReturnContext: Ref<ProcessReturnContext | null> = ref(null)

  async function getAllPoliciesByPartnerProxyId(
    partnerProxyId: string
  ): Promise<Policy[]> {
    policyIDs.value = null
    // reset available DDSLines, will be recalculated below (overwritten also on error)
    availableDdsLines.value = new Set<DDSLine>()

    try {
      policyIDs.value = (await fetchAllPoliciesByPartnerProxyId(partnerProxyId)).map(
        (policy) => policy.policyId
      )

      const fetchAll = policyIDs.value.map((t_policyId) =>
        getPolicyByPolicyId(t_policyId)
      )

      const results = await Promise.allSettled(fetchAll)
      const allFulfilled = results
        .filter((x) => x.status === 'fulfilled')
        .map((x: any) => x.value)
      const filteredAllFulfilled = removeEmptyPolices(allFulfilled)

      availableDdsLines.value = extractDdsLines(filteredAllFulfilled)

      return sortPoliciesList(filteredAllFulfilled) ?? []
    } catch (error) {
      availableDdsLines.value = new Set<DDSLine>()
      throw error
    }
  }

  // check if a given DDSLine is present in any of the currently loaded policies
  function hasDdsLine(ddsLine: DDSLine): boolean {
    return availableDdsLines.value.has(ddsLine)
  }

  async function getLockedContractsCount(partnerProxyId: string): Promise<number> {
    if (lockedContractsCount.value) return lockedContractsCount.value
    else lockedContractsCount.value = await fetchLockedContractsCount(partnerProxyId)
    return lockedContractsCount.value
  }

  async function getQuoteDetailsMapByPartnerProxyId(
    partnerProxyId: string
  ): Promise<Map<string, ShowOrDeleteQuotes>> {
    if (quotesDetails.value.size > 0) return quotesDetails.value
    else {
      // to get quote details we need all quoteIds first
      quoteIds.value = await fetchAllQuoteIdsByPartnerProxyId(partnerProxyId)
      // if there is none, return the empty map
      if (quoteIds.value?.length === 0) return quotesDetails.value
      // process all quotes and check if they are valid to be shown or delete them
      await processAllQuotes(quoteIds.value)
    }
    return quotesDetails.value
  }

  async function getOdsContractByContractId(contractId: string) {
    let t_odsContracts = odsContracts.value.get(contractId)

    if (!t_odsContracts) {
      t_odsContracts = await fetchOdsContractByContractId(contractId)
      odsContracts.value.set(contractId, t_odsContracts)
    }
    return t_odsContracts
  }

  async function processAllQuotes(quoteIds: string[]) {
    // check each quoteId if it is valid to be shown
    for (const t_quoteId of quoteIds) {
      try {
        const single_quote_response: ShowOrDeleteQuotes =
          await fetchQuoteShowOrDelete(t_quoteId)
        if (single_quote_response.showCard)
          quotesDetails.value.set(t_quoteId, single_quote_response)
        if (single_quote_response.deleteFromQuotes) {
          await deleteQuoteIdFromList(t_quoteId)
        }

        if (quotesDetails.value.size == 0) return []
      } catch (error) {
        console.error('Error in processAllQuotes', {
          quoteId: sanitizeForLog(t_quoteId),
          error: sanitizeForLog(error)
        })
        return
      }
    }
  }

  // set the pre-selected policy ids (e.g. coming from another view)
  function setPreSelectedPolicyIds(ids: string[] | null): void {
    preSelectedPolicyIds.value = (ids ?? []).filter(Boolean)
  }

  // set the pre-selected policy id for backwards compatibility
  function setPreSelectedPolicyId(id: string | null): void {
    setPreSelectedPolicyIds(id ? [id] : [])
  }

  // reset all pre-selected policy ids
  function resetPreSelectedPolicyIds(): void {
    preSelectedPolicyIds.value = []
  }

  // reset the pre-selected policy id for backwards compatibility
  function resetPreSelectedPolicyId(): void {
    resetPreSelectedPolicyIds()
  }

  // consume all pre-selected policy ids and clear them (one-time read)
  function consumePreSelectedPolicyIds(): string[] {
    const ids = [...preSelectedPolicyIds.value]
    preSelectedPolicyIds.value = []
    return ids
  }

  // consume the first pre-selected policy id for backwards compatibility
  function consumePreSelectedPolicyId(): string | null {
    return consumePreSelectedPolicyIds()[0] ?? null
  }

  const IVK_THANK_YOU_STORAGE_KEY = 'ivk-thank-you-values'

  function readIvkThankYouValuesFromSessionStorage(): IvkThankYouValues | null {
    try {
      const storedValues = sessionStorage.getItem(IVK_THANK_YOU_STORAGE_KEY)
      return storedValues ? (JSON.parse(storedValues) as IvkThankYouValues) : null
    } catch (error) {
      console.warn('IVK-Danke-Werte konnten nicht aus sessionStorage gelesen werden.', {
        errorType: error instanceof Error ? error.name : typeof error
      })
      return null
    }
  }

  function removeIvkThankYouValuesFromSessionStorage(): void {
    try {
      sessionStorage.removeItem(IVK_THANK_YOU_STORAGE_KEY)
    } catch (error) {
      console.warn('IVK-Danke-Werte konnten nicht aus sessionStorage gelöscht werden.', {
        errorType: error instanceof Error ? error.name : typeof error
      })
    }
  }

  // set the IVK thank-you values (mail + licensePlate) for one-time use on ThankYouPage
  function setIvkThankYouValues(values: IvkThankYouValues): void {
    ivkThankYouValues.value = values
    try {
      sessionStorage.setItem(IVK_THANK_YOU_STORAGE_KEY, JSON.stringify(values))
    } catch (error) {
      console.warn(
        'IVK-Danke-Werte konnten nicht in sessionStorage gespeichert werden.',
        { errorType: error instanceof Error ? error.name : typeof error }
      )
    }
  }

  // consume the IVK thank-you values and clear them (one-time read)
  function consumeIvkThankYouValues(): IvkThankYouValues | null {
    const values = ivkThankYouValues.value ?? readIvkThankYouValuesFromSessionStorage()
    removeIvkThankYouValuesFromSessionStorage()
    return values
  }

  // set the digital-card thank-you values (mail + licensePlate) for one-time use on ThankYouPage
  function setDigitalCardThankYouValues(values: DigitalCardThankYouValues): void {
    digitalCardThankYouValues.value = values
  }

  // consume the digital-card thank-you values and clear them (one-time read)
  function consumeDigitalCardThankYouValues(): DigitalCardThankYouValues | null {
    return digitalCardThankYouValues.value
  }

  // set the accompanied-driving thank-you values (licensePlate) for one-time use on ThankYouPage
  function setAccompaniedDrivingThankYouValues(
    values: AccompaniedDrivingThankYouValues
  ): void {
    accompaniedDrivingThankYouValues.value = values
  }

  // consume the accompanied-driving thank-you values and clear them (one-time read)
  function consumeAccompaniedDrivingThankYouValues(): AccompaniedDrivingThankYouValues | null {
    return accompaniedDrivingThankYouValues.value
  }

  // set the vehicle-change thank-you values (conclusionNumber + evbNumber) for one-time use on ThankYouPage
  function setVehicleChangeThankYouValues(values: VehicleChangeThankYouValues): void {
    vehicleChangeThankYouValues.value = values
  }

  // consume the vehicle-change thank-you values and clear them (one-time read)
  function consumeVehicleChangeThankYouValues(): VehicleChangeThankYouValues | null {
    return vehicleChangeThankYouValues.value
  }

  // reset all thank-you values at once (e.g. after a delay or when leaving the page)
  function resetThankYouValues(): void {
    resetThankYouValuesTimer = setTimeout(() => {
      ivkThankYouValues.value = null
      removeIvkThankYouValuesFromSessionStorage()
      digitalCardThankYouValues.value = null
      accompaniedDrivingThankYouValues.value = null
      vehicleChangeThankYouValues.value = null
    }, 30000)
  }

  const PROCESS_RETURN_CONTEXT_STORAGE_KEY = 'self-service-process-return-context'

  function setProcessReturnContext(
    process: SelfServiceProcess,
    policyIds: string[]
  ): void {
    const context: ProcessReturnContext = {
      process,
      policyIds: [...policyIds]
    }
    processReturnContext.value = context

    try {
      sessionStorage.setItem(PROCESS_RETURN_CONTEXT_STORAGE_KEY, JSON.stringify(context))
    } catch (error) {
      console.warn('Self-Service-Rückkehrkontext konnte nicht gespeichert werden.', {
        errorType: error instanceof Error ? error.name : typeof error
      })
    }
  }

  function readProcessReturnContextFromSessionStorage(): ProcessReturnContext | null {
    try {
      const storedContext = sessionStorage.getItem(PROCESS_RETURN_CONTEXT_STORAGE_KEY)
      if (!storedContext) return null

      const parsedContext = JSON.parse(storedContext) as {
        process: SelfServiceProcess
        policyIds?: string[]
        vsnrNumbers?: string[]
      }
      const policyIds = parsedContext.policyIds ?? parsedContext.vsnrNumbers

      return Array.isArray(policyIds)
        ? { process: parsedContext.process, policyIds: [...policyIds] }
        : null
    } catch (error) {
      console.warn('Self-Service-Rückkehrkontext konnte nicht gelesen werden.', {
        errorType: error instanceof Error ? error.name : typeof error
      })
      return null
    }
  }

  function clearProcessReturnContextStorage(): void {
    try {
      sessionStorage.removeItem(PROCESS_RETURN_CONTEXT_STORAGE_KEY)
    } catch (error) {
      console.warn('Self-Service-Rückkehrkontext konnte nicht gelöscht werden.', {
        errorType: error instanceof Error ? error.name : typeof error
      })
    }
  }

  function getProcessReturnContext(clear = false): ProcessReturnContext | null {
    const storedContext =
      processReturnContext.value ?? readProcessReturnContextFromSessionStorage()
    const context = storedContext
      ? { process: storedContext.process, policyIds: [...storedContext.policyIds] }
      : null

    if (context) processReturnContext.value = context
    if (clear) {
      processReturnContext.value = null
      clearProcessReturnContextStorage()
    }
    return context
  }

  function resetProcessReturnContext(): void {
    processReturnContext.value = null
    clearProcessReturnContextStorage()
  }

  return {
    availableDdsLines,
    consumeAccompaniedDrivingThankYouValues,
    consumeDigitalCardThankYouValues,
    consumeIvkThankYouValues,
    consumePreSelectedPolicyId,
    consumePreSelectedPolicyIds,
    consumeVehicleChangeThankYouValues,
    getAllPoliciesByPartnerProxyId,
    getLockedContractsCount,
    getOdsContractByContractId,
    getPolicyByPolicyId,
    getProcessReturnContext,
    getQuoteDetailsMapByPartnerProxyId,
    hasDdsLine,
    policyDetailURL,
    processAllQuotes,
    resetPreSelectedPolicyId,
    resetPreSelectedPolicyIds,
    resetProcessReturnContext,
    resetThankYouValues,
    setAccompaniedDrivingThankYouValues,
    setDigitalCardThankYouValues,
    setIvkThankYouValues,
    setPreSelectedPolicyId,
    setPreSelectedPolicyIds,
    setProcessReturnContext,
    setVehicleChangeThankYouValues
  }
})
